"use client"

import { useEffect, useLayoutEffect, useState } from "react"

import dynamic from "next/dynamic"
import { useSearchParams } from "next/navigation"

import { useInlineSignup } from "@/components/flow/SignupEntry"
import { isCheckoutSheetOpen, isSignupSheetOpen } from "@/features/checkout/checkout-sheet"
import { CHECKOUT_TAP_EVENT, dismissCheckoutHold, holdCheckout, isCheckoutHoldPending } from "@/lib/checkout/pdp-checkout-link"
import { useIsWebView } from "@/lib/providers/WebViewProvider"

const PREFETCH_DELAY_MS = 2000
const MAGIC_LINK_SOURCES = new Set(["magic_link", "magic_link_failed"])

let isCheckoutClientReady = false

const loadCheckoutClient = () =>
  import("@/features/checkout/components/CheckoutClientEntry").then((module) => {
    isCheckoutClientReady = true
    return module.CheckoutClientEntry
  })

const CheckoutClient = dynamic(loadCheckoutClient)

interface SheetParams {
  get: (key: string) => string | null
}

export const needsCheckoutClient = (searchParams: SheetParams, isWebView: boolean, inlineSignup = false): boolean =>
  isCheckoutSheetOpen(searchParams, isWebView) || (inlineSignup && isSignupSheetOpen(searchParams, isWebView)) || MAGIC_LINK_SOURCES.has(searchParams.get("source") ?? "")

const loadOnCheckoutTap = () => {
  if (typeof window === "undefined") return
  if (isCheckoutHoldPending()) {
    void loadCheckoutClient()
    return
  }
  window.addEventListener(CHECKOUT_TAP_EVENT, () => void loadCheckoutClient(), { once: true })
}

loadOnCheckoutTap()

const prefetchCheckoutClient = (): (() => void) => {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(() => void loadCheckoutClient())
    return () => window.cancelIdleCallback(handle)
  }
  const handle = window.setTimeout(() => void loadCheckoutClient(), PREFETCH_DELAY_MS)
  return () => window.clearTimeout(handle)
}

export const LazyCheckoutClient = () => {
  const searchParams = useSearchParams()
  const isWebView = useIsWebView()
  const isSheetOpen = isCheckoutSheetOpen(searchParams, isWebView)
  const inlineSignup = useInlineSignup()
  const needed = needsCheckoutClient(searchParams, isWebView, inlineSignup)
  const [hasMounted, setHasMounted] = useState(needed)
  useEffect(() => {
    if (needed) setHasMounted(true)
  }, [needed])
  useEffect(() => (isWebView ? undefined : prefetchCheckoutClient()), [isWebView])
  useLayoutEffect(() => {
    if (isSheetOpen && !isCheckoutClientReady) holdCheckout("tap")
  }, [isSheetOpen])
  useEffect(() => {
    if (!isSheetOpen) dismissCheckoutHold("open")
  }, [isSheetOpen])
  if (!needed && !hasMounted) return null
  return <CheckoutClient />
}
